import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { getErrorByPath } from 'modules/shared/utils';
import { useEffect, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { TASK_ASSIGNMENT_ERRORS, TASK_ASSIGNMENT_FIELDS, TASK_TYPE_LABEL } from '../../constants';
import { filterTasksByType } from '../utils';
import TaskAssignmentModal from './TaskAssignmentModal';

/**
 * TaskAssignmentField
 * @param {boolean} disabled - Whether the field is disabled.
 * @param {Array} tasks - The list of tasks.
 * @param {string} taskType - The type of task.
 * @param {boolean} isEditMode - Whether the field is in edit mode.
 * @param {string} taskName - The name of the task.
 * @returns {JSX.Element}
 */
export default function TaskAssignmentField({ disabled, tasks, taskType, isEditMode, taskName }) {
  const { watch, setValue, clearErrors } = useFormContext();

  const { errors } = useFormState();
  const errorMessage = getErrorByPath(errors, TASK_ASSIGNMENT_ERRORS.TASKS);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = watch(TASK_ASSIGNMENT_FIELDS.ASSIGNED_TASKS) || [];

  const label = isEditMode
    ? taskName
    : value.length === 0
      ? `Select ${TASK_TYPE_LABEL[taskType]} tasks`
      : `${value.length} ${value.length === 1 ? 'task' : 'tasks'} selected`;

  const [showModal, setShowModal] = useState(false);

  const filteredTasks = filterTasksByType(tasks, taskType);

  useEffect(() => {
    if (value.length > 0) {
      clearErrors(TASK_ASSIGNMENT_ERRORS.TASKS);
    }
  }, [value, clearErrors]);

  return (
    <>
      <Button
        type='button'
        variant='outlined'
        disabled={disabled}
        onClick={() => setShowModal(true)}
        sx={{
          padding: '6px 12px',
          borderRadius: 1.5,
          fontWeight: 400,
          color: 'grey.500',
          borderColor: 'grey.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          '&:hover': { backgroundColor: 'grey.100' },
          '&:disabled': {
            cursor: 'default',
            borderColor: 'grey.300',
            opacity: 0.6,
          },
          width: '60%',
          maxWidth: '360px',
        }}
      >
        {label}
      </Button>

      <TaskAssignmentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        tasks={filteredTasks}
        taskType={taskType}
        selectedTasks={value}
        onConfirm={(selectedTasks) => {
          setValue(TASK_ASSIGNMENT_FIELDS.ASSIGNED_TASKS, selectedTasks, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setShowModal(false);
        }}
      />
      {errorMessage && <FormHelperText error>{String((errorMessage as any)?.message || errorMessage || "")}</FormHelperText>}
    </>
  );
}
